import { useEffect, useRef, useState } from 'react';

import { Field } from 'react-final-form';

import {
  Button,
  Chip,
  IconButton,
  Icons,
  Input,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';

import { RffForm } from '@cast/shared-rff-ui';

const OTHER_OPTION = 'Other...';

const NEGATIVE_FEEDBACK_OPTIONS = [
  'Not factually correct',
  "Didn't follow instructions",
  'Not useful',
  OTHER_OPTION,
] as const;

type FeedbackOption = (typeof NEGATIVE_FEEDBACK_OPTIONS)[number];

type NegativeFeedbackFormProps = {
  onClose: () => void;
  onSelect: (reason: string, details?: string) => void;
};

type SelectedOption = FeedbackOption | null;

const useInputRef = (selectedOption: SelectedOption) => {
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (selectedOption === OTHER_OPTION && inputRef.current) {
      inputRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      inputRef.current.focus();
    }
  }, [selectedOption]);
  return inputRef;
};

export const NegativeFeedbackForm = ({
  onClose,
  onSelect,
}: NegativeFeedbackFormProps) => {
  const [selectedOption, setSelectedOption] = useState<SelectedOption>(null);

  const inputRef = useInputRef(selectedOption);

  return (
    <Stack
      p={16}
      gap={8}
      border="1px solid"
      borderColor={tokens.theme.border.structural.neutral}
      borderRadius="4px"
      bgcolor={tokens.theme.bg.structural.surface}
    >
      <Stack
        direction="row"
        gap={8}
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="P14M" color={tokens.theme.fg.structural.primary}>
          Tell us more
        </Typography>
        <IconButton
          type="button"
          variant="ghost"
          size="sm"
          aria-label="Close feedback form"
          onClick={onClose}
        >
          <Icons.X size={16} color={tokens.theme.fg.action.onGhost} />
        </IconButton>
      </Stack>
      <Stack gap={4} direction="row" flexWrap="wrap">
        {NEGATIVE_FEEDBACK_OPTIONS.map((option) => {
          const isSelected = selectedOption === option;
          return (
            <Chip
              key={option}
              size="md"
              onClick={() => {
                if (option === OTHER_OPTION) {
                  setSelectedOption(OTHER_OPTION);
                } else {
                  onSelect(option);
                }
              }}
              sx={{
                border: '1px solid',
                borderColor: isSelected
                  ? tokens.theme.bg.categorical.greyStrong
                  : tokens.theme.border.structural.neutral,
                backgroundColor: isSelected
                  ? tokens.theme.bg.categorical.greyStrong
                  : tokens.theme.bg.structural.surfaceRaised,
                color: isSelected
                  ? tokens.theme.fg.categorical.onGreyStrong
                  : tokens.theme.fg.structural.primary,
                '&:hover': {
                  borderColor: tokens.theme.bg.categorical.greyStrong,
                  backgroundColor: tokens.theme.bg.categorical.greyStrong,
                  color: tokens.theme.fg.categorical.onGreyStrong,
                },
              }}
            >
              {option}
            </Chip>
          );
        })}
      </Stack>
      {selectedOption === OTHER_OPTION && (
        <RffForm
          initialValues={{ text: '' }}
          onSubmit={({ text }: { text: string }) =>
            onSelect(OTHER_OPTION, text)
          }
        >
          {({ values }) => (
            <Stack gap={4}>
              <Typography
                variant="P10M"
                color={tokens.theme.fg.structural.primary}
              >
                How can we improve this response?
              </Typography>
              <Stack direction="row" gap={8} alignItems="center">
                <Field name="text">
                  {({ input }) => (
                    <Input
                      {...input}
                      inputRef={inputRef}
                      placeholder="Please add details"
                      aria-label="Feedback details"
                      size="small"
                      sx={{ flex: 1 }}
                    />
                  )}
                </Field>
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  disabled={!values.text?.trim()}
                >
                  Submit
                </Button>
              </Stack>
            </Stack>
          )}
        </RffForm>
      )}
    </Stack>
  );
};
